/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/exhaustive-deps */
/**
 * A React component that displays a current event tab.
 * @returns The rendered current event tab component.
 */
import { debounce } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";

import CurrentEventTabComponent from "./currentEventTabComponent";

import { getCloudinaryMediaBaseUrl } from "../../../config/cloudinaryBase";
import {
  buildStaffClubEventQuery,
  eventsForManagedClubOnly,
} from "../../../helpers/eventClubScope";
import { pickEventDisplayNumber } from "../../../helpers/eventDisplayNumber";
import { clubListResponce } from "../../../store/features/club/clubSlice";
import { currentEventListResponce } from "../../../store/features/event/eventSlice";
import { showToast } from "../../../store/features/toast/toastSlice";
import Calender from "../../calender/calender";
import useAuth from "../../../hooks/useAuth";
import useWindowDimension from "../../hooks/useWindowDimension";
import Constant from "../../../util/constant";

const TABLE_BODY_MIN_SCROLL_PX = 280;
const TABLE_BODY_VIEWPORT_BOTTOM_GAP_PX = 24;

const CurrentEventTabContainer = (props) => {
  /**
   * Initializes various state variables and references for the event table component.
   * @returns None
   */
  const { t } = useTranslation();
  // const data = useSelector((state) => (state?.curruntEvent?.curruntEvent)); // Access the specific property you need
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const eventsDataRes = useSelector(
    (state) => state?.event?.currentEventListResponce
  );
  const clubList = useSelector((state) => state?.club?.clubList ?? []);
  const isAdmin =
    window.localStorage.getItem("role") === Constant.ROLES.ADMIN;
  const { managedClubId, isClubStaff } = useAuth();
  const [eventScopeFilter, setEventScopeFilter] = useState("all");
  const [selectedClubFilterId, setSelectedClubFilterId] = useState(null);
  const tableContainerRef = useRef(null);
  const [eventsData, seteventsData] = useState([]);
  const smarttableref = useRef();
  const dimensions = useWindowDimension();
  const pageSizeRef = useRef(15);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [tableBodyScrollHeight, setTableBodyScrollHeight] = useState(() =>
    typeof window !== "undefined"
      ? Math.max(TABLE_BODY_MIN_SCROLL_PX, window.innerHeight - 320)
      : 400
  );
  const [artistmultiarray, setArtistmultiarray] = useState([]);
  const [categorymultiarray, setCategorymultiarray] = useState([]);

  const [selectedStatus, setSelectedStatus] = useState(null);

  //multisearch
  const [placeHolder, setPlaceHolder] = useState(true);
  const [filteredEventData, setFilteredEventData] = useState([]);

  const [screenWidth, setScreenWidth] = useState(window.screen.width);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [window.screen.width]);

  /**
   * useEffect hook that sets the placeholder state to false after a delay of 2000 milliseconds.
   * This is useful for displaying a temporary placeholder content before the actual content is loaded.
   * @returns None
   */
  useEffect(() => {
    if (isAdmin || isClubStaff) {
      dispatch(clubListResponce());
    }
  }, [dispatch, isAdmin, isClubStaff]);

  useEffect(() => {
    if (!Array.isArray(eventsDataRes)) {
      seteventsData([]);
      return;
    }
    const scoped =
      isClubStaff && managedClubId
        ? eventsForManagedClubOnly(eventsDataRes, managedClubId)
        : eventsDataRes;
    const clubMap = Object.fromEntries(
      (clubList || []).map((c) => [c._id, `${c.name} (${c.slug})`])
    );
    seteventsData(
      scoped.map((item) => {
        const cid = item?.clubid || item?.clubId;
        return {
          ...item,
          clubLabel: cid ? clubMap[cid] || cid : t("PLATFORM_WIDE"),
          languages: item?.languageList
            ?.map((val) => val?.titleenglish)
            .join(", "),
          genreList: item?.genre?.map((val) => val?.title).join(", "),
          artistList: item?.artistsDetails
            ?.map((inner) => inner?.name)
            .join(", "),
          categoryList: item?.category?.map((cat) => cat?.title).join(", "),
          dateList:
            moment(item?.startdatetime)
              .format("DD MMM YY, h:mm A")
              ?.toString() +
            "," +
            moment(item?.enddatetime).format("DD MMM YY, h:mm A")?.toString(),
        };
      })
    );
  }, [eventsDataRes, clubList, t, isClubStaff, managedClubId]);

  useEffect(() => {
    if (Array.isArray(eventsData)) {
      const lowercaseSearchValue = searchValue?.toLowerCase();

      const filteredData = eventsData?.filter(
        (item) =>
          item?.title?.toLowerCase()?.includes(lowercaseSearchValue) ||
          item?.ticketprice?.toString()?.includes(lowercaseSearchValue) ||
          item?.location?.toLowerCase()?.includes(lowercaseSearchValue) ||
          pickEventDisplayNumber(item)
            .toLowerCase()
            .includes(lowercaseSearchValue)
      );

      setFilteredEventData(filteredData);
    }
  }, [searchValue, eventsData]);

  const fetchCurrentEvents = useCallback(() => {
    const staffQ = buildStaffClubEventQuery(managedClubId);
    if (isClubStaff) {
      const params = staffQ ?? {};
      dispatch(currentEventListResponce(params)).then((resultAction) => {
        setPlaceHolder(false);
        if (currentEventListResponce.rejected.match(resultAction)) {
          const apiMsg =
            typeof resultAction.payload === "string"
              ? resultAction.payload
              : "";
          dispatch(
            showToast({
              message: apiMsg || t("EVENTS_CLUB_FORBIDDEN"),
              type: "error",
            })
          );
        }
      });
      return;
    }
    const params = {};
    if (isAdmin) {
      if (eventScopeFilter === "platform") params.eventscope = "platform";
      if (eventScopeFilter === "club" && selectedClubFilterId) {
        params.clubid = selectedClubFilterId;
      }
    }
    dispatch(currentEventListResponce(params)).then((resultAction) => {
      if (currentEventListResponce.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (currentEventListResponce.rejected.match(resultAction)) {
        setPlaceHolder(false);
      }
    });
  }, [
    dispatch,
    isAdmin,
    isClubStaff,
    managedClubId,
    eventScopeFilter,
    selectedClubFilterId,
    t,
  ]);

  useEffect(() => {
    fetchCurrentEvents();
  }, [fetchCurrentEvents]);

  const onTableFilterChange = (filterText) => {
    setSearchValue(filterText);
  };

  /**
   * Clears the search filter by programmatically clicking the clear button.
   * @returns None
   */
  const searchFilterClear = () => {
    smarttableref.current.querySelectorAll("button")[1].click();
  };

  /**
   * Clears the search input by programmatically clicking the clear search button.
   * @returns None
   */
  const ClearSearchButton = () => {
    smarttableref.current.querySelectorAll("button")[0].click();
  };

  /**
   * Initializes state variables for start date and end date.
   * Converts the start date string to a Date object.
   * Defines the format for the start date.
   * Formats the start date using moment.js library.
   * @param {{string}} startDate - The start date string.
   * @returns None
   */
  const [startDate, setStartDate] = useState("");

  /**
   * An array of column objects used for displaying data in a table.
   * Each column object contains a label, key, and optional filter and sorting properties.
   * @type {Array}
   */
  const [dateRange, setDateRange] = useState([null, null]);
  const [startRangeDate, endRangeDate] = dateRange;
  const CustomLabel = ({ value, onClick }) => {
    return (
      <div className="inputDatepickerRow">
        <button type="button" className="customDateInput" onClick={onClick}>
          {startRangeDate && endRangeDate ? (
            <>
              <span className="startDateStyle">
                {moment(startRangeDate).format("D/MM/YY")}
              </span>
              <span className="endDateStyle">
                {moment(endRangeDate).format("D/MM/YY")}
              </span>
            </>
          ) : (
            <span className="customDatePlaceholder">{t("SELECT_DATE")}</span>
          )}
        </button>
      </div>
    );
  };
  const columns = [
    {
      label: "",
      key: "image",
      filter: false,
      sorting: false,
    },
    {
      label: (
        <>
          {t("EVENT_DETAILS")}{" "}
          <span className="thCount">
            {searchValue === null
              ? eventsData?.length
              : filteredEventData?.length}
          </span>
        </>
      ),
      key: "title",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("LOCATION")}</>,
      key: "location",
      filter: false,
      sorting: false,
    },
    {
      key: "languages",
      filter: false,
      sorting: false,
    },
    {
      key: "artistList",
      filter: false,
      sorting: false,
    },
    {
      key: "categoryList",
      filter: false,
      sorting: false,
    },
    {
      key: "dateList",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("ARTIST")}</>,
      key: "artistsDetails",
      filter: (values, onChange) => {
        const getUniqueArtistsDetails = (events) => {
          const uniqueArtistsDetails = {};
          events.forEach((event) => {
            event.forEach((artistDetail) => {
              if (!uniqueArtistsDetails[artistDetail._id]) {
                uniqueArtistsDetails[artistDetail._id] = {
                  label: artistDetail.name,
                  value: artistDetail.name,
                };
              }
            });
          });
          const uniqueArtistsDetailsArray = Object.values(uniqueArtistsDetails);
          return uniqueArtistsDetailsArray;
        };

        const uniqueArtistsDetails = getUniqueArtistsDetails(values);
        const handleChange = (selected) => {
          setArtistmultiarray(selected);
          const selectedValues = selected.map((elementArtist) => {
            return elementArtist.value;
          });

          onChange((item) => {
            const _filterValue = item?.filter((innerItem) => {
              return selectedValues?.includes(innerItem?.name);
            });

            return Array.isArray(selectedValues) && selectedValues.length
              ? selectedValues.includes(_filterValue[0]?.name)
              : true;
          });
        };

        const CustomOption = ({ children, ...props }) => {
          return (
            <components.Option {...props}>
              <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
              />
              <label htmlFor="checkbox">
                <span></span>
              </label>
              {children}
            </components.Option>
          );
        };

        return (
          <div
            className={
              artistmultiarray?.length > 1
                ? "multipleselectWrapper customeSelectWrapper"
                : "customeSelectWrapper"
            }
          >
            <Select
              isMulti
              onChange={handleChange}
              closeMenuOnSelect={false}
              placeholder={t("SELECT")}
              options={uniqueArtistsDetails}
              hideSelectedOptions={false}
              components={{ Option: CustomOption }}
              className="react-select-container multiSelecter"
              classNamePrefix="react-select"
              isSearchable={false}
            />
            {artistmultiarray?.length > 1 && (
              <p className="multiselectTag">
                +{artistmultiarray?.slice(1)?.length}
              </p>
            )}
          </div>
        );
      },

      sorter: false,
    },
    {
      label: <>{t("TYPE")}</>,
      key: "category",
      filter: (values, onChange) => {
        const getUniqueCategoryDetails = (events) => {
          const uniqueCategoryDetails = {};
          events.forEach((event) => {
            event.forEach((category) => {
              if (!uniqueCategoryDetails[category._id]) {
                uniqueCategoryDetails[category._id] = {
                  label: category.title,
                  value: category.title,
                };
              }
            });
          });
          const uniqueCategoryDetailsArray = Object.values(
            uniqueCategoryDetails
          );
          return uniqueCategoryDetailsArray;
        };
        const uniqueCategoryDetails = getUniqueCategoryDetails(values);
        const handleChange = (selected) => {
          setCategorymultiarray(selected);
          const selectedValues = selected.map((elementCategory) => {
            return elementCategory.value;
          });

          onChange((item) => {
            const _filterValue = item?.filter((innerItem) => {
              return selectedValues?.includes(innerItem?.title);
            });

            return Array.isArray(selectedValues) && selectedValues?.length
              ? selectedValues.includes(_filterValue[0]?.title)
              : true;
          });
        };

        const CustomOption = ({ children, ...props }) => {
          return (
            <components.Option {...props}>
              <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
              />
              <label htmlFor="checkbox">
                <span></span>
              </label>
              {children}
            </components.Option>
          );
        };

        return (
          <div
            className={
              categorymultiarray?.length > 1
                ? "multipleselectWrapper customeSelectWrapper"
                : "customeSelectWrapper"
            }
          >
            <Select
              isMulti
              onChange={handleChange}
              closeMenuOnSelect={false}
              placeholder={t("SELECT")}
              options={uniqueCategoryDetails}
              hideSelectedOptions={false}
              components={{ Option: CustomOption }}
              className="react-select-container multiSelecter"
              classNamePrefix="react-select"
              isSearchable={false}
            />
            {categorymultiarray?.length > 1 && (
              <p className="multiselectTag">
                +{categorymultiarray?.slice(1)?.length}
              </p>
            )}
          </div>
        );
      },
      sorting: false,
    },
    {
      label: <>{t("EVENT_TIMINGS")}</>,
      key: "startdatetime",
      filter: (itemExpiryDates, onChange) => {
        const handleDateFilterChange = (date, selectedValue, isStartDate) => {
          if (!date) return;
          const currentDate = new Date(date);
          const dateOnly = isNaN(currentDate)
            ? null
            : new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate()
            );
          if (isStartDate) {
            setStartDate(dateOnly);
          }

          onChange((e) => {
            const listDate = new Date(e);
            if (currentDate == "Invalid Date") {
              if (selectedValue === "startDate") {
                return true;
              } else if (selectedValue === "endDate" && listDate > startDate) {
                return true;
              } else {
                return false;
              }
            }
            if (selectedValue === "startDate" && listDate > currentDate) {
              setStartDate(currentDate);

              return true;
            } else if (
              selectedValue === "endDate" &&
              listDate < currentDate &&
              listDate > startDate &&
              startDate !== null
            ) {
              return true;
            } else {
              return false;
            }
          });
        };

        return (
          <div className="calenderFilterStyle">
            <Calender
              selectsRange={true}
              startDate={startRangeDate}
              endDate={endRangeDate}
              onChange={(update) => {
                const [newStartDate, newEndDate] = update;
                if (newStartDate) {
                  handleDateFilterChange(newStartDate, "startDate", true);
                }
                if (newEndDate) {
                  handleDateFilterChange(newEndDate, "endDate", false);
                }
                setDateRange(update);
              }}
              selected={startRangeDate}
              customInput={<CustomLabel />}
              value={endRangeDate}
            />
          </div>
        );
      },
      sorting: false,
    },
    {
      label: (
        <>{dimensions.width < 1631 ? t("TICKET_DETAILS") : t("TICKET_PRICE")}</>
      ),
      key: "ticketprice",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("TICKETS_SOLD")}</>,
      key: "ticketssold",
      filter: false,
      sorting: false,
    },

    {
      label: <>{t("STATUS")}</>,
      key: "eventstatus",
      filter: (values, onChange) => {
        const unique = [...new Set(values)].sort();
        const statusOptions = unique.map((element) => ({
          value: element,
          label: element,
        }));

        const handleChange = (selected) => {
          const selectedValue = selected ? selected.value : null;

          if (selectedValue === selectedStatus) {
            setSelectedStatus(null);
            onChange(() => true); // Reset the filter
          } else {
            setSelectedStatus(selectedValue);
            onChange((item) =>
              selectedValue !== null ? item === selectedValue : true
            );
          }

          // This should work only when clicking on the selected option again
          if (selectedValue === selectedStatus) {
            smarttableref.current.querySelectorAll("button")[0].click();
          }
        };

        const CustomOption = ({ children, ...props }) => {
          return (
            <components.Option {...props}>
              <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
              />
              <label for="checkbox">
                <span></span>
              </label>
              {children}
            </components.Option>
          );
        };

        return (
          <Select
            isMulti={false}
            onChange={handleChange}
            closeMenuOnSelect={false}
            isClearable={true}
            placeholder={t("SELECT")}
            options={statusOptions}
            defaultValue={selectedStatus}
            hideSelectedOptions={false}
            components={{ Option: CustomOption }}
            className="react-select-container singleSelecter"
            classNamePrefix="react-select"
            isSearchable={false}
          />
        );
      },
      sorting: false,
    },
    ...(isAdmin
      ? [
          {
            label: <>{t("CLUB_COLUMN")}</>,
            key: "clubLabel",
            filter: false,
            sorting: false,
          },
        ]
      : []),
    {
      label: <>{t("EVENT_ORGANISER")}</>,
      key: "organizer",
      filter: false,
      sorting: false,
    },
  ];

  /**
   * Size the scrollable tbody from the viewport: avoids wrong global thead/breadcrumb
   * measurements and updates when the tab becomes visible or the window resizes.
   */
  useLayoutEffect(() => {
    const tbodyEl = tableContainerRef.current;
    if (!tbodyEl) return undefined;

    const updateScrollHeight = () => {
      const el = tableContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const top = rect.top;
      const next = Math.max(
        TABLE_BODY_MIN_SCROLL_PX,
        window.innerHeight -
          top -
          TABLE_BODY_VIEWPORT_BOTTOM_GAP_PX
      );
      setTableBodyScrollHeight(next);
    };

    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(updateScrollHeight);
    });
    ro.observe(tbodyEl);
    window.addEventListener("resize", updateScrollHeight);
    updateScrollHeight();

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateScrollHeight);
    };
  }, [loading, placeHolder]);

  /**
   * Handles the click event on a row in the events list.
   * Sets the addEventBtn state to 'edit' and navigates to the event detail page.
   * @param {{object}} item - The item associated with the clicked row.
   * @returns None
   */
  const onRowClick = (item) => {
    navigate("/events-list/event-detail", { state: { item, page: "edit" } });
  };

  /**
   * Loads more items when triggered. It sets the loading state to true, simulates a delay of 2 seconds,
   * and then sets the loading state to false and increases the page size by 2.
   * @returns None
   */

  const loadMoreItems = () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      pageSizeRef.current += 10;
    }, 2000);
  };

  /**
   * Sets the add event button state and navigates to the "add-event" page with the provided item.
   * @param {{any}} item - The item to pass to the "add-event" page.
   * @returns None
   */
  const addEventFunction = (item) => {
    navigate("/events-list/add-event", { state: { item, page: "add" } });
  };

  const addEventFunctionOne = (item) => {
    navigate("/events-list/add-event", { state: { page: "add" } });
  };

  /**
   * Debounces the handleTableScroll function to prevent it from being called too frequently.
   * When the table container is scrolled, it checks if the scroll position is at the bottom
   * and if there are more items to load. If both conditions are met, it calls the loadMoreItems function.
   * @returns None
   */

  const handleTableScroll = debounce(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const isAtBottom =
        tableContainer.scrollTop + tableContainer.clientHeight >=
        tableContainer.scrollHeight;
      if (isAtBottom && eventsData.length > pageSizeRef.current) {
        loadMoreItems();
      }
    }
  }, 200);

  /**
   * Adds an event listener to the table container element to handle scroll events.
   * The event listener is removed when the component is unmounted.
   * @param {{function}} handleTableScroll - The function to handle the table scroll event.
   * @returns None
   */

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleTableScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [handleTableScroll]);

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  const scopeFilterOptions = [
    { value: "all", label: t("EVENT_FILTER_ALL") },
    { value: "platform", label: t("EVENT_FILTER_PLATFORM") },
    { value: "club", label: t("EVENT_FILTER_BY_CLUB") },
  ];
  const clubFilterSelectOptions = (clubList || []).map((c) => ({
    value: c._id,
    label: `${c.name} (${c.slug})${
      c.isactive === false ? ` [${t("CLUB_STATUS_INACTIVE")}]` : ""
    }`,
  }));
  const selectedScopeOption = scopeFilterOptions.find(
    (o) => o.value === eventScopeFilter
  );
  const selectedClubOption =
    selectedClubFilterId &&
    clubFilterSelectOptions.find((o) => o.value === selectedClubFilterId);

  return (
    <CurrentEventTabComponent
      smarttableref={smarttableref}
      columns={columns}
      eventsData={eventsData}
      clearSearchButton={ClearSearchButton}
      emptyImg={CLOUDINARY_URL + "ukldodwmn2h0c9bynbpm.png"}
      emptyTitle={t("NO_CURRENT_EVENTS_YET")}
      noDataFound={t("NO_DATA_FOUND")}
      noDataTitle={t("NO_DATA_FOUND")}
      noDataDesc={t("SERACH_NT-FOUND_EVENT_LIST")}
      emptyDataDescTow={t("PLEASE_CLICK_ON_THEBELOW_BUTTON_TO_ADD_EVENT")}
      emptyDataDesc={t("STILL_NO_EVENT_HAS_BEEN_ADDED")}
      noDataAddButtonTitle={t("ADD_EVENT")}
      onTableFilterChange={onTableFilterChange}
      placeHolderShimer={placeHolder}
      itemsPerPage={pageSizeRef.current}
      searchFilterClear={searchFilterClear}
      dimensions={dimensions}
      tableBodyScrollHeight={tableBodyScrollHeight}
      searchValue={searchValue}
      loading={loading}
      onRowClick={(item) => {
        onRowClick(item);
      }}
      tableContainerRef={tableContainerRef}
      addEventFunction={addEventFunction}
      emptyDataAddButton={addEventFunctionOne}
      noDataDescTwo={t("PLEASE_SEARCH_TRY_AGAIN_OR_ADD_EVENT")}
      screenWidth={screenWidth}
      showAdminEventFilters={isAdmin}
      eventScopeOptions={scopeFilterOptions}
      eventScopeValue={selectedScopeOption}
      onEventScopeChange={(opt) => {
        const v = opt?.value || "all";
        setEventScopeFilter(v);
        if (v !== "club") setSelectedClubFilterId(null);
      }}
      clubFilterOptions={clubFilterSelectOptions}
      selectedClubFilter={selectedClubOption}
      onClubFilterChange={(opt) =>
        setSelectedClubFilterId(opt?.value ?? null)
      }
      showClubsShortcut={isAdmin}
      onNavigateToClubs={() => navigate("/clubs-management")}
    />
  );
};

export default CurrentEventTabContainer;
